import {
  Check,
  ChevronDown,
  ChevronUp,
  Droplets,
  FlaskConical,
  Pencil,
  Plus,
  Trash2
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useGetCipSipChemicalTypes } from "../../../api/static-data";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import { Badge } from "../../../components/ui/common/badge";
import { Button } from "../../../components/ui/common/button";
import { Card, CardContent, CardHeader } from "../../../components/ui/common/card";
import { Form } from "../../../components/ui/common/form";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../components/ui/common/tooltip";
import { FormInput } from "../../../components/ui/Input/FormInput/FormInput";
import { FormInputNumber } from "../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../components/ui/Input/FormSelector/FormSelect";
import { PER_TYPES, PER_WEIGHT_UNITS } from "../../../config/constants";
import { AccordionTypes } from "../../../helpers/AccordionHelper";
import { validateCIPSIPFrequency, validateCIPSIPNumericField } from "../../../utils/utils";

interface SipContentProps {
  getSipData: (data: unknown) => void;
  handleCIPValidation?: () => boolean;
  currentCipSipData: any;
  accordionType: AccordionTypes | null;
  lockStatus?: boolean;
  handleAccordion: (requestType: AccordionTypes) => void;
  disabled?: boolean;
}

type SipFormValues = {
  frequency: {
    amount: number;
    per: string;
  };
  productionAmount: {
    amount: number;
    per: string;
    perUnit: string;
  };
  energyConsumption: number;
  steamFlow: number;
  steamPressure: number;
  wasteWater: number;
  waterConsumption: number;
  manufacturer: string;
  modelNumber: string;
  concentration: number;
  consumption: number;
  chemicalType: string;
};

const SipContentContent = ({
  getSipData,
  lockStatus = false,
  accordionType,
  currentCipSipData,
  handleAccordion,
  disabled = false
}: SipContentProps) => {
  const { data: cipSipChemicalDataType } = useGetCipSipChemicalTypes();

  const [filteredChemicals, setFilteredChemicals] = useState(cipSipChemicalDataType);
  const [chemicalData, setChemicalData] = useState<any>([]);
  const [enableEdit, setEnableEdit] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [enableUpdateButton, setEnableUpdateButton] = useState<any>(false);
  const [open, setOpen] = useState(accordionType === AccordionTypes.CIP_SIP);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [chemicalToDelete, setChemicalToDelete] = useState<{ index: number; name: string } | null>(
    null
  );

  // Sync accordion open state with accordionType prop
  useEffect(() => {
    setOpen(accordionType === AccordionTypes.CIP_SIP);
  }, [accordionType]);

  const values: any = useMemo(() => {
    setChemicalData(currentCipSipData?.[0]?.chemical || []);
    setFilteredChemicals(cipSipChemicalDataType);
    return {
      energyConsumption: currentCipSipData?.[0]?.energyConsumption,
      manufacturer: currentCipSipData?.[0]?.manufacturer,
      modelNumber: currentCipSipData?.[0]?.modelNumber,
      steamFlow: currentCipSipData?.[0]?.steamFlow,
      steamPressure: currentCipSipData?.[0]?.steamPressure,
      wasteWater: currentCipSipData?.[0]?.wasteWater,
      waterConsumption: currentCipSipData?.[0]?.waterConsumption,
      frequency: {
        amount: currentCipSipData?.[0]?.frequency?.amount || "",
        per: currentCipSipData?.[0]?.frequency?.per || "week"
      },
      productionAmount: {
        amount: currentCipSipData?.[0]?.productionAmount?.amount || "",
        per: currentCipSipData?.[0]?.productionAmount?.per || "week",
        perUnit: currentCipSipData?.[0]?.productionAmount?.perUnit || "kg"
      },
      concentration: "",
      consumption: "",
      chemicalType: ""
    };
  }, [currentCipSipData, cipSipChemicalDataType]);

  const form = useForm<SipFormValues>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    criteriaMode: "all",
    shouldFocusError: true,
    defaultValues: values,
    values: values
  });

  const { handleSubmit, getValues, setValue, control, watch } = form;

  const chemicalType = watch("chemicalType");

  const onSubmit = (data: any) => {
    data.chemical = chemicalData;
    delete data.concentration;
    delete data.consumption;
    delete data.chemicalType;

    getSipData([data]);
    handleAccordion(AccordionTypes.CIP_SIP);
  };

  const addNewChemical = () => {
    const chemicalTypeValue = getValues("chemicalType");
    const concentration = getValues("concentration");
    const consumption = getValues("consumption");

    // Validate that chemical type is selected
    if (!chemicalTypeValue) {
      return;
    }

    const createNewChemicalData = () => ({
      chemicalName: filteredChemicals.find((item: any) => item.value === chemicalTypeValue)?.text,
      concentration: concentration,
      consumption: consumption
    });

    const newChemicalData = createNewChemicalData();

    if (enableEdit) {
      const updatedChemicalData = chemicalData.map((item: any, index: number) =>
        index === deleteIndex ? { ...item, ...newChemicalData } : item
      );
      setChemicalData(updatedChemicalData);
      setEnableEdit(false);
    } else {
      setChemicalData((prevData: any) => [...prevData, newChemicalData]);
    }

    setValue("concentration", 0);
    setValue("consumption", 0);
    setValue("chemicalType", "");
  };

  const onEditHandler = (item: any, index: number) => {
    setEnableEdit(true);
    setDeleteIndex(index);
    setValue("concentration", item.concentration);
    setValue("consumption", item.consumption);
    const chemicalValue = cipSipChemicalDataType?.find(
      (data: any) => data.text === item.chemicalName
    )?.value;
    setValue("chemicalType", chemicalValue);
  };

  return (
    <Form {...form}>
      <Card
        className={`tw-border-border ${
          disabled ? "tw-opacity-50 tw-pointer-events-none" : ""
        } tw-pt-0 tw-pb-0 tw-mt-1`}
      >
        <CardHeader
          className="tw-bg-secondary/50 tw-py-3.5 tw-px-3.5 tw-rounded-t-lg tw-cursor-pointer"
          onClick={() => setOpen(v => !v)}
        >
          <div className="tw-flex tw-items-center tw-justify-between">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Droplets className="tw-h-4 tw-w-4 tw-text-primary" />
              <span className="tw-text-sm tw-font-semibold tw-text-foreground">CIP / SIP</span>
              {chemicalData.length > 0 && (
                <Badge variant="outline" className="tw-text-[10px] tw-h-5 tw-px-1.5">
                  {chemicalData.length}
                </Badge>
              )}
            </div>
            {open ? (
              <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            ) : (
              <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            )}
          </div>
        </CardHeader>
        {open && (
          <CardContent className="tw-py-3.5 tw-px-3.5 tw-space-y-5">
            {/* Form Fields */}
            <p className="tw-text-[10px] tw-text-muted-foreground">Add new CIP/SIP record</p>

            <div className="tw-border tw-border-dashed tw-border-primary/30 tw-rounded-md tw-p-3 tw-space-y-3 tw-bg-primary/5">
              {/* Row 1: Frequency, Time(s) per */}
              <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
                <div>
                  <FormInputNumber
                    label="Frequency"
                    control={control}
                    placeholder="0"
                    name="frequency.amount"
                    required
                    removeDecimal
                    errorMessage="Frequency is required"
                    rules={{
                      validate: {
                        valueCheck: (v: any) => validateCIPSIPFrequency(v)
                      }
                    }}
                    disabled={lockStatus}
                  />
                </div>
                <div>
                  <FormSelect
                    label="Time(s) per"
                    control={control}
                    name="frequency.per"
                    options={PER_TYPES}
                    placeholder="Select time period"
                    disabled={lockStatus}
                  />
                </div>
              </div>

              {/* Row 2: Production Amount, Unit, Per */}
              <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-3">
                <div>
                  <FormInputNumber
                    label="Production Amount"
                    control={control}
                    placeholder="0"
                    name="productionAmount.amount"
                    required
                    errorMessage="Production Amount is required"
                    rules={{
                      validate: {
                        valueCheck: (v: any) => validateCIPSIPNumericField(v)
                      }
                    }}
                    disabled={lockStatus}
                  />
                </div>
                <div>
                  <FormSelect
                    label="Unit"
                    control={control}
                    name="productionAmount.perUnit"
                    options={PER_WEIGHT_UNITS}
                    placeholder="Select unit"
                    disabled={lockStatus}
                  />
                </div>
                <div>
                  <FormSelect
                    label="Per"
                    control={control}
                    name="productionAmount.per"
                    options={PER_TYPES}
                    placeholder="Select period"
                    disabled={lockStatus}
                  />
                </div>
              </div>

              {/* Row 3: Manufacturer, Model Number */}
              <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
                <div>
                  <FormInput
                    label="Manufacturer"
                    control={control}
                    placeholder="Manufacturer"
                    name="manufacturer"
                    disabled={lockStatus}
                  />
                </div>
                <div>
                  <FormInput
                    label="Model Number"
                    control={control}
                    placeholder="Model Number"
                    name="modelNumber"
                    disabled={lockStatus}
                  />
                </div>
              </div>

              {/* Row 4: Energy Consumption, Steam Flow, Steam Pressure */}
              <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-3">
                <div>
                  <FormInputNumber
                    label="Energy Consumption"
                    control={control}
                    placeholder="0"
                    name="energyConsumption"
                    required
                    errorMessage="Energy Consumption is required"
                    rightContentEnable
                    rightContent="kWh"
                    rules={{
                      validate: {
                        valueCheck: (v: any) => validateCIPSIPNumericField(v)
                      }
                    }}
                    disabled={lockStatus}
                  />
                </div>
                <div>
                  <FormInputNumber
                    label="Steam Flow"
                    control={control}
                    placeholder="0"
                    name="steamFlow"
                    required
                    errorMessage="Steam Flow is required"
                    rightContentEnable
                    rightContent="kg/hr"
                    rules={{
                      validate: {
                        valueCheck: (v: any) => validateCIPSIPNumericField(v)
                      }
                    }}
                    disabled={lockStatus}
                  />
                </div>
                <div>
                  <FormInputNumber
                    label="Steam Pressure"
                    control={control}
                    placeholder="0"
                    name="steamPressure"
                    required
                    errorMessage="Steam Pressure is required"
                    rightContentEnable
                    rightContent="bar"
                    rules={{
                      validate: {
                        valueCheck: (v: any) => validateCIPSIPNumericField(v)
                      }
                    }}
                    disabled={lockStatus}
                  />
                </div>
              </div>

              {/* Row 5: Water Consumption, Waste Water */}
              <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
                <div>
                  <FormInputNumber
                    label="Water Consumption"
                    control={control}
                    placeholder="0"
                    name="waterConsumption"
                    required
                    errorMessage="Water Consumption is required"
                    rightContentEnable
                    rightContent="L"
                    rules={{
                      validate: {
                        valueCheck: (v: any) => validateCIPSIPNumericField(v)
                      }
                    }}
                    disabled={lockStatus}
                  />
                </div>
                <div>
                  <FormInputNumber
                    label="Waste Water"
                    control={control}
                    placeholder="0"
                    name="wasteWater"
                    required
                    errorMessage="Waste Water is required"
                    rightContentEnable
                    rightContent="L"
                    rules={{
                      validate: {
                        valueCheck: (v: any) => validateCIPSIPNumericField(v)
                      }
                    }}
                    disabled={lockStatus}
                  />
                </div>
              </div>
            </div>

            {/* Chemical Data List */}
            {chemicalData.length > 0 && (
              <div className="tw-space-y-2">
                <p className="tw-text-[10px] tw-text-muted-foreground tw-font-medium tw-flex tw-items-center tw-gap-1.5">
                  <FlaskConical className="tw-h-3 tw-w-3" />
                  Added Chemicals ({chemicalData.length})
                </p>
                {chemicalData.map((item: any, index: number) => (
                  <div
                    key={`chemical_${index}`}
                    className="tw-flex tw-items-center tw-justify-between tw-rounded-md tw-border tw-bg-muted/30 tw-p-1"
                  >
                    <div className="tw-flex-1 tw-grid tw-grid-cols-3 tw-gap-3 tw-text-sm">
                      <div>
                        <span className="tw-text-[10px] tw-text-muted-foreground">Chemical</span>
                        <p className="tw-font-medium tw-text-foreground">{item?.chemicalName ?? ""}</p>
                      </div>
                      <div>
                        <span className="tw-text-[10px] tw-text-muted-foreground">Concentration</span>
                        <p className="tw-font-mono tw-text-foreground">{item?.concentration ?? ""}</p>
                      </div>
                      <div>
                        <span className="tw-text-[10px] tw-text-muted-foreground">Consumption</span>
                        <p className="tw-font-mono tw-text-foreground">{item?.consumption ?? ""}</p>
                      </div>
                    </div>
                    <div className="tw-flex tw-gap-1 tw-shrink-0 tw-ml-3">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="tw-h-7 tw-w-7 tw-text-muted-foreground hover:tw-text-foreground"
                              onClick={() => {
                                onEditHandler(item, index);
                                setEnableUpdateButton(true);
                              }}
                              disabled={disabled || lockStatus}
                            >
                              <Pencil className="tw-h-3 tw-w-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="tw-h-7 tw-w-7 tw-text-destructive"
                        onClick={() => {
                          const chemicalName = item.chemicalName || "this chemical";
                          setChemicalToDelete({ index, name: chemicalName });
                          setDeleteModalOpen(true);
                        }}
                        disabled={disabled || lockStatus}
                      >
                        <Trash2 className="tw-h-3 tw-w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Chemical Form */}
            <div className="tw-border tw-border-dashed tw-border-primary/30 tw-rounded-md tw-p-3 tw-space-y-3 tw-bg-primary/5">
              <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-3">
                <div>
                  <FormSelect
                    label="Chemical Name"
                    control={control}
                    name="chemicalType"
                    options={cipSipChemicalDataType || []}
                    placeholder="Select Chemical"
                    disabled={lockStatus}
                  />
                </div>
                <div>
                  <FormInputNumber
                    label="Concentration"
                    control={control}
                    placeholder="0"
                    name="concentration"
                    rules={{
                      validate: {
                        valueCheck: (v: any) => {
                          // Only validate if field has a value
                          if (!v) return true;
                          return validateCIPSIPNumericField(v);
                        }
                      }
                    }}
                    disabled={lockStatus}
                  />
                </div>
                <div>
                  <FormInputNumber
                    label="Consumption"
                    control={control}
                    placeholder="0"
                    name="consumption"
                    rules={{
                      validate: {
                        valueCheck: (v: any) => {
                          // Only validate if field has a value
                          if (!v) return true;
                          return validateCIPSIPNumericField(v);
                        }
                      }
                    }}
                    disabled={lockStatus}
                  />
                </div>
              </div>
              <div className="tw-flex tw-justify-end">
                <Button
                  size="sm"
                  onClick={() => {
                    addNewChemical();
                    setEnableUpdateButton(false);
                  }}
                  disabled={!chemicalType || disabled || lockStatus}
                  className="tw-gap-1 tw-text-xs tw-h-7"
                >
                  {enableUpdateButton ? (
                    <>
                      <Check className="tw-h-3.5 tw-w-3.5" />
                      Update Chemical
                    </>
                  ) : (
                    <>
                      <Plus className="tw-h-3 tw-w-3" />
                      Add Chemical
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Action Button */}
            <div className="tw-flex tw-justify-end tw-pt-2">
              <Button
                size="sm"
                onClick={handleSubmit(onSubmit)}
                disabled={lockStatus || disabled}
                className="tw-gap-1 tw-text-xs tw-h-7 hover:tw-bg-primary/90"
              >
                <Check className="tw-h-3.5 tw-w-3.5" />
                Save CIP/SIP Data
              </Button>
            </div>
          </CardContent>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModalOpen && chemicalToDelete && (
          <ConfirmModal
            viewModal={deleteModalOpen}
            closeModal={() => {
              setDeleteModalOpen(false);
              setChemicalToDelete(null);
            }}
            cancel={() => {
              setDeleteModalOpen(false);
              setChemicalToDelete(null);
            }}
            approve={() => {
              const updatedChemicalData = chemicalData.filter(
                (_: any, i: any) => i !== chemicalToDelete.index
              );
              setChemicalData(updatedChemicalData);
              setDeleteModalOpen(false);
              setChemicalToDelete(null);
            }}
            title={`Delete CIP/SIP Chemical`}
            subTitle={`Are you sure you want to remove "${chemicalToDelete.name}"? This action cannot be undone.`}
            style={{ zIndex: 10001 }}
            className="tw-delete-confirmation-modal"
            mountNode={document.body}
          />
        )}
      </Card>
    </Form>
  );
};

export default SipContentContent;
